import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { monitoringApi } from '../api';
import type { EmployeeWithUser } from '../../hr/types';
import { agentReleasesApi, AgentRelease } from '../../agent-releases/api';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  employees: EmployeeWithUser[];
  onIssued: () => void;
}

export function IssueTokenDialog({ open, onOpenChange, employees, onIssued }: Props) {
  const [employeeId, setEmployeeId] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [issued, setIssued] = React.useState<string | null>(null);
  const [release, setRelease] = React.useState<AgentRelease | null>(null);

  React.useEffect(() => {
    if (open) {
      setEmployeeId(employees[0]?.id ?? '');
      setLabel(''); setIssued(null); setRelease(null);
    }
  }, [open, employees]);

  React.useEffect(() => {
    if (!issued) return;
    agentReleasesApi.getTenant()
      .then(setRelease)
      .catch(() => { /* silent — install-links section just won't appear */ });
  }, [issued]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!employeeId) { toast.error('Select an employee'); return; }
    setBusy(true);
    try {
      const result = await monitoringApi.issueAgentToken({ employee_id: employeeId, agent_label: label || null });
      setIssued(result.plaintext);
      onIssued();
    } catch (err: any) {
      toast.error('Issue failed', { description: err.message });
    } finally { setBusy(false); }
  }

  async function copy() {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy — copy manually');
    }
  }

  function buildDeepLink(token: string): string {
    const server = window.location.origin;
    return `worqly-agent://setup?token=${encodeURIComponent(token)}&server=${encodeURIComponent(server)}`;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{issued ? 'Token issued' : 'Issue agent token'}</DialogTitle>
          {!issued && <DialogDescription>Pick an employee and a short label (e.g. "Office desktop").</DialogDescription>}
        </DialogHeader>

        {!issued ? (
          <form onSubmit={submit} className="grid gap-4 pt-2">
            <div className="grid gap-1.5">
              <Label>Employee</Label>
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Label</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Office desktop" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy ? 'Issuing…' : 'Issue token'}</Button>
            </div>
          </form>
        ) : (
          <div className="grid gap-4 pt-2">
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 dark:bg-amber-950/40 dark:border-amber-900/60 dark:text-amber-300">
              <strong>Copy now.</strong> The token is shown only once. After closing this dialog there's no way to retrieve it — issue a new one if lost.
            </p>
            <div className="grid gap-1.5">
              <Label className="text-xs">Agent token</Label>
              <Input readOnly value={issued} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
              <Button variant="outline" size="sm" onClick={copy} className="justify-self-start">Copy token</Button>
            </div>

            {release && (release.macos_url || release.windows_url) && (
              <div className="grid gap-2 border-t pt-3">
                <Label className="text-xs">Install the desktop agent</Label>
                <p className="text-xs text-muted-foreground">
                  Download for your OS, install, then either open the setup link below or paste the token manually.
                </p>
                <div className="flex flex-wrap gap-2">
                  {release.macos_url && (
                    <Button asChild variant="outline" size="sm">
                      <a href={release.macos_url} target="_blank" rel="noreferrer">Download for macOS</a>
                    </Button>
                  )}
                  {release.windows_url && (
                    <Button asChild variant="outline" size="sm">
                      <a href={release.windows_url} target="_blank" rel="noreferrer">Download for Windows</a>
                    </Button>
                  )}
                  <Button asChild variant="outline" size="sm">
                    <a href={buildDeepLink(issued)}>Open in agent (setup link)</a>
                  </Button>
                </div>
                {release.version && (
                  <p className="text-[11px] text-muted-foreground">Version {release.version}{release.published_at ? ` · published ${new Date(release.published_at).toLocaleDateString()}` : ''}</p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
